import { getIdToken, getJWTCookieClient } from '@/utils/firebase-helpers';

/**
 * Synchronous authentication token resolver (JWT cookie or config only).
 * Use this when you need immediate token resolution and can't use async.
 *
 * Retrieves authentication token following a priority order:
 * 1. JWT from cookie (client-side)
 * 2. Provided config token (server-side or fallback)
 *
 * @param configToken - Optional fallback token from configuration
 * @returns Authentication token or empty string if none available
 */
export function resolveAuthTokenSync(configToken?: string): string {
  // Priority 1: Try to get JWT from cookie (client-side only)
  if (typeof window !== 'undefined') {
    const cookieToken = getJWTCookieClient();
    if (cookieToken !== '') {
      return cookieToken;
    }
  }

  // Priority 2: Use provided config token (server-side or fallback)
  return configToken ?? '';
}

/**
 * Unified authentication token resolver for HTTP services.
 * This is the preferred method as it checks all authentication sources.
 *
 * Retrieves authentication token following a priority order:
 * 1. Firebase ID token (client-side)
 * 2. JWT from cookie (client-side)
 * 3. Provided config token (server-side or fallback)
 *
 * @param configToken - Optional fallback token from configuration
 * @returns Authentication token or empty string if none available
 */
export async function resolveAuthToken(configToken?: string): Promise<string> {
  // Priority 1: Try to get Firebase ID token (client-side only)
  if (typeof window !== 'undefined') {
    try {
      const firebaseToken = await getIdToken();
      if (firebaseToken !== null && firebaseToken !== '') {
        return firebaseToken;
      }
    } catch (error) {
      console.warn('[AuthTokenResolver] Failed to get Firebase token:', error);
    }

    // Priority 2: Try to get JWT from cookie (client-side only)
    const cookieToken = getJWTCookieClient();
    if (cookieToken !== '') {
      return cookieToken;
    }
  }

  // Priority 3: Use provided config token (server-side or fallback)
  return configToken ?? '';
}

/**
 * Creates authorization headers with resolved token
 * @param configToken - Optional fallback token from configuration
 * @returns Headers object with authorization if token is available
 */
export async function createAuthHeaders(configToken?: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = await resolveAuthToken(configToken);
  if (token !== '') {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}
